import { PasswordInput } from '@/components/PasswordInput'
import { SectionCard } from '@/components/SectionCard'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { isApiError } from '@/utils/error'
import { showError, showSuccess } from '@/utils/toast'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { CheckCircle2, Circle } from 'lucide-react'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { authApi } from '../api/authApi'
import { useAuth } from '../hooks/useAuth'

type ChangePasswordForm = {
  oldPassword: string
  newPassword: string
  confirmPassword: string
}

export function ChangePasswordView() {
  const { t } = useTranslation()
  const { signout } = useAuth()
  const changePassword = useMutation({
    mutationFn: authApi.changePassword,
  })

  const schema = useMemo(
    () =>
      z
        .object({
          oldPassword: z.string().min(1, t('auth.currentPasswordRequired')),
          newPassword: z
            .string()
            .min(8, t('auth.passwordRuleLength'))
            .max(20, t('auth.passwordRuleLength'))
            .regex(/[A-Z]/, t('auth.passwordRuleUppercase'))
            .regex(/[a-z]/, t('auth.passwordRuleLowercase'))
            .regex(/[0-9]/, t('auth.passwordRuleNumeric')),
          confirmPassword: z.string().min(1, t('auth.confirmPasswordRequired')),
        })
        .refine(data => data.newPassword !== data.oldPassword, {
          path: ['newPassword'],
          message: t('auth.passwordMustBeDifferent'),
        })
        .refine(data => data.newPassword === data.confirmPassword, {
          path: ['confirmPassword'],
          message: t('auth.passwordsDoNotMatch'),
        }),
    [t],
  )

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ChangePasswordForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  })
  const newPassword = watch('newPassword') || ''
  const passwordChecks = [
    {
      label: t('auth.passwordRuleLengthShort'),
      passed: newPassword.length >= 8 && newPassword.length <= 20,
    },
    {
      label: t('auth.passwordRuleUppercaseShort'),
      passed: /[A-Z]/.test(newPassword),
    },
    {
      label: t('auth.passwordRuleLowercaseShort'),
      passed: /[a-z]/.test(newPassword),
    },
    {
      label: t('auth.passwordRuleNumericShort'),
      passed: /[0-9]/.test(newPassword),
    },
  ]

  const onSubmit = async ({ oldPassword, newPassword }: ChangePasswordForm) => {
    try {
      await changePassword.mutateAsync({ oldPassword, newPassword })
      showSuccess(t('auth.changePasswordSuccess'))
      await signout().catch(() => undefined)
    } catch (error) {
      if (isApiError(error)) {
        showError(error.message)
        return
      }
      showError(t('auth.changePasswordError'))
    }
  }

  return (
    <div className="max-w-4xl">
      <SectionCard
        title={t('auth.changePassword')}
        description={t('auth.changePasswordDescription')}
        cardClass="overflow-hidden"
      >
        <form data-testid="change-password-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_280px]">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="oldPassword">{t('auth.currentPassword')}</Label>
                <PasswordInput
                  data-testid="change-password-old"
                  id="oldPassword"
                  autoComplete="current-password"
                  {...register('oldPassword')}
                  className={errors.oldPassword ? 'border-red-500' : ''}
                />
                {errors.oldPassword && <p data-testid="form-field-error" className="text-sm text-red-500">{errors.oldPassword.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">{t('auth.newPassword')}</Label>
                <PasswordInput
                  data-testid="change-password-new"
                  id="newPassword"
                  autoComplete="new-password"
                  {...register('newPassword')}
                  className={errors.newPassword ? 'border-red-500' : ''}
                />
                {errors.newPassword && <p data-testid="form-field-error" className="text-sm text-red-500">{errors.newPassword.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('auth.confirmNewPassword')}</Label>
                <PasswordInput
                  data-testid="change-password-confirm"
                  id="confirmPassword"
                  autoComplete="new-password"
                  {...register('confirmPassword')}
                  className={errors.confirmPassword ? 'border-red-500' : ''}
                />
                {errors.confirmPassword && <p data-testid="form-field-error" className="text-sm text-red-500">{errors.confirmPassword.message}</p>}
              </div>
            </div>

            <div className="rounded-lg border border-border/50 bg-secondary/30 p-4">
              <div className="mb-3 text-xs font-medium text-foreground">{t('auth.passwordRequirements')}</div>
              <ul className="space-y-2">
                {passwordChecks.map(check => {
                  const Icon = check.passed ? CheckCircle2 : Circle

                  return (
                    <li
                      key={check.label}
                      className={
                        check.passed
                          ? 'flex items-start gap-2 text-xs text-green-600'
                          : 'flex items-start gap-2 text-xs text-muted-foreground'
                      }
                    >
                      <Icon className="mt-0.5 size-3.5 shrink-0" />
                      <span>{check.label}</span>
                    </li>
                  )
                })}
              </ul>
            </div>
          </div>

          <div className="flex justify-end border-t border-border/50 pt-4">
            <Button data-testid="change-password-submit" type="submit" disabled={changePassword.isPending}>
              {changePassword.isPending ? t('auth.changingPassword') : t('auth.changePassword')}
            </Button>
          </div>
        </form>
      </SectionCard>
    </div>
  )
}
